import React from 'react';
import {
	Control,
	FieldErrors,
	FieldValues,
	UseFormClearErrors,
	UseFormSetValue,
	UseFormTrigger,
	UseFormWatch,
} from 'react-hook-form';
import { isAfter, isBefore } from 'date-fns';

import { Button, DatepickerRange, Select, SpinnerOverlay } from '@components/common';

import { useQueryPortalAvailableMilestones } from '@hooks/portal/tasks/useQueryPortalAvailableMilestones';
import { useQueryPortalTasksAssignees } from '@hooks/portal/tasks/useQueryPortalTasksAssignees';

import { portalTasks as portalTasksSelector } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { parseStringToDateInMilliseconds } from '@utils/kpi-utils/date';
import { checkIsTaskInMilestoneTimeRange } from '@components/ModalComponents/MilestoneForm/utils';

import { TSelectOption } from '@interfaces/components/componentTypes';

import { datePickerErrorText, milestoneWarningText } from './constants';
import { fieldsToTestDetails, tabsNames, taskFormFields } from '@components/ModalComponents/TaskForm/constants';

import CustomCalendarDay from './CustomCalendarDay';
import MilestoneSelectOption from './MilestoneSelectOption';
import PrioritySelectOption from './PrioritySelectOption';
import {
	getMaxDateEndDate,
	getMaxDateStartDate,
	getMinDateEndDate,
	getMinDateStartDate,
	getTooltipForDisabledDay,
} from './utils';

import styles from './Details.module.scss';

interface IDetailsProps {
	isEditMode: boolean;
	isViewMode: boolean;
	errors: FieldErrors;
	setValue: UseFormSetValue<FieldValues>;
	clearErrors: UseFormClearErrors<FieldValues>;
	watch: UseFormWatch<FieldValues>;
	trigger: UseFormTrigger<FieldValues>;
	control: Control;
	isDetailsTabError: boolean;
	setCurrentTab: (value: string) => void;
	projectId?: string;
	portalTasksStatuses: TSelectOption[];
	portalTasksPriorities: TSelectOption[];
}

const Details = ({
	control,
	errors,
	watch,
	setValue,
	clearErrors,
	isEditMode,
	isViewMode,
	isDetailsTabError,
	trigger,
	setCurrentTab,
	projectId,
	portalTasksStatuses,
	portalTasksPriorities,
}: IDetailsProps) => {
	const { linkedTasks } = useAppSelector(portalTasksSelector);

	const selectedProjectId = (isEditMode || isViewMode) ? projectId : watch(taskFormFields.project)?.value;

	const { portalTasksAssignees, isLoadingPortalTasksAssignees } = useQueryPortalTasksAssignees(selectedProjectId);

	const {
		portalAvailableMilestones,
		isLoadingPortalAvailableMilestones,
	} = useQueryPortalAvailableMilestones(selectedProjectId);

	const availableMilestonesOptions = portalAvailableMilestones.map(milestone => {
		return {
			...milestone,
			label: `${milestone.title} (${milestone.startDate} - ${milestone.endDate})`,
		};
	});

	const sortedTasksPriorities = portalTasksPriorities.sort((a, b) => {
		if (!a?.weight) return 1;

		if (!b?.weight) return -1;

		return a.weight - b.weight;
	});

	const isLoading = isLoadingPortalTasksAssignees || isLoadingPortalAvailableMilestones;

	const setStartDate = (date: number) => {
		if (date) clearErrors(taskFormFields.startDate);

		setValue(taskFormFields.startDate, date, { shouldDirty: true });
	};
	const setEndDate = (date: number) => {
		if (date) clearErrors(taskFormFields.endDate);

		setValue(taskFormFields.endDate, date, { shouldDirty: true });
	};

	const onChangeAssignees = (options: TSelectOption<string>[]) => setValue(taskFormFields.assignees, options);

	const assigneesValue = watch(taskFormFields.assignees);
	const milestoneValue = watch(taskFormFields.milestone);
	const startDateValue = watch(taskFormFields.startDate);
	const endDateValue = watch(taskFormFields.endDate);

	const isMilestoneWarningText = milestoneValue && !checkIsTaskInMilestoneTimeRange({
		milestoneStartDate: parseStringToDateInMilliseconds(milestoneValue.startDate),
		milestoneEndDate: parseStringToDateInMilliseconds(milestoneValue.endDate),
		associatedTaskStartDate: startDateValue,
		associatedTaskEndDate: endDateValue,
	});

	const onSubmit = async () => {
		const isValid = await trigger(fieldsToTestDetails);

		if (!isValid) return;

		if (!isEditMode) setCurrentTab(tabsNames.dependencies);
	};

	const onClickBackHandler = () => {
		setCurrentTab(tabsNames.basicInfo);
	};

	const maxDateFrom = getMaxDateStartDate(linkedTasks);
	const minDateFrom = getMinDateStartDate(linkedTasks);
	const maxDateTo = getMaxDateEndDate(linkedTasks);
	const minDateTo = getMinDateEndDate(linkedTasks);

	const disabledDayTooltipText = getTooltipForDisabledDay(linkedTasks);

	const renderDayContentsFrom = (day: number, date: Date) => {
		const isDisabled = !!((minDateFrom && isBefore(date, minDateFrom))
			|| (maxDateFrom && isAfter(date, maxDateFrom)));

		return (
			<CustomCalendarDay
				date={date}
				day={day}
				isDisabled={isDisabled}
				tooltipText={disabledDayTooltipText}
			/>
		);
	};

	const renderDayContentsTo = (day: number, date: Date) => {
		const isDisabled = !!((minDateTo && isBefore(date, minDateTo))
			|| (maxDateTo && isAfter(date, maxDateTo)));

		return (
			<CustomCalendarDay
				date={date}
				day={day}
				isDisabled={isDisabled}
				tooltipText={disabledDayTooltipText}
			/>
		);
	};

	return (
		<>
			{isLoading && <SpinnerOverlay />}
			<div className={styles.rowContainer}>
				<div className={styles.status}>
					<Select
						control={control}
						disabled={isViewMode}
						errorMessage={errors.status?.message}
						isError={!!errors?.status}
						label='Status'
						name={taskFormFields.status}
						options={portalTasksStatuses}
						placeholder='Choose status'
						required
					/>
				</div>
				<div className={styles.priority}>
					<Select
						control={control}
						customComponents={{
							Option: (props) => PrioritySelectOption({
								...props,
							}),
						}}
						disabled={isViewMode}
						errorMessage={errors.priority?.message}
						isError={!!errors?.priority}
						label='Priority'
						name={taskFormFields.priority}
						options={sortedTasksPriorities}
						placeholder='Choose priority'
						required
					/>
				</div>
			</div>
			<div className={styles.rowContainer}>
				<div className={styles.milestone}>
					<Select
						control={control}
						customComponents={{
							Option: (props) => MilestoneSelectOption({
								...props,
							}),
						}}
						disabled={isViewMode}
						errorMessage={errors.milestone?.message}
						isClearable
						isError={!!errors?.milestone}
						label='Milestone'
						name={taskFormFields.milestone}
						options={availableMilestonesOptions}
						placeholder='Choose milestone'
					/>
				</div>
				<div className={styles.datePickersContainer}>
					<DatepickerRange
						disableInput
						disabled={isViewMode}
						errorFromDate={errors?.startDate?.message as string}
						errorMessage={datePickerErrorText}
						errorToDate={errors?.endDate?.message as string}
						fromLabel='Start date'
						handleChangeFromDate={setStartDate}
						handleChangeToDate={setEndDate}
						isClearableFrom
						isClearableTo
						isMobileDevice={false}
						maxDateFrom={maxDateFrom ? new Date(maxDateFrom) : undefined}
						maxDateTo={maxDateTo ? new Date(maxDateTo) : undefined}
						minDateFrom={minDateFrom ? new Date(minDateFrom) : undefined}
						minDateTo={minDateTo ? new Date(minDateTo) : undefined}
						renderDayContentsFrom={renderDayContentsFrom}
						renderDayContentsTo={renderDayContentsTo}
						toLabel='End date'
						value={{
							from: watch(taskFormFields.startDate) as number,
							to: watch(taskFormFields.endDate) as number,
						}}
					/>
				</div>
			</div>
			{isMilestoneWarningText && <div className={styles.warning}>{milestoneWarningText}</div>}
			<div className={styles.rowContainer}>
				<div className={styles.assignees}>
					<Select
						closeMenuOnSelect={false}
						control={control}
						disabled={isViewMode}
						handleChange={onChangeAssignees}
						isError={!!errors.assignees}
						isMulti
						label='Assignees'
						multiselectText='assignees selected'
						name={taskFormFields.assignees}
						options={portalTasksAssignees}
						placeholder='Select assignees'
						value={assigneesValue}
					/>
				</div>
			</div>
			{!isEditMode && !isViewMode
				&& <div className={styles.buttons}>
					<Button
						onClick={onClickBackHandler}
						text='Back'
						withoutBackground
					/>
					<Button
						disabled={isDetailsTabError}
						onClick={onSubmit}
						text='Next'
					/>
				</div>
			}
		</>
	);
};

export default Details;
